import { Box, Dialog, DialogTitle, Typography } from '@material-ui/core';
import { PayloadAction } from '@reduxjs/toolkit';
import { DialogVariantEnum } from 'enums/dialogVariant';
import React, { Component } from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { subscribeActionAfter } from 'redux-subscribe-action';
import { dialogClose, dialogOpen } from 'store/dialog/dialog.actions';
import { IDialogOpenPayload } from 'store/dialog/dialog.interface';
import CancelUnsuccessful from './Content/CancelUnsuccessful';
import ChangeDueDate from './Content/ChangeDueDate';
import ContentChangedCms from './Content/ContentChangedCms';
import ContentChangedProjectCms from './Content/ContentChangedProjectCms';
import ContentRemovedCms from './Content/ContentRemovedCms';
import InTranslation from './Content/InTranslation';
import JobAction, { IJobActionProps } from './Content/JobAction';
import ModifyLanguage from './Content/ModifyLanguage';
import NoStatus from './Content/NoStatus.tsx';
import NoSupportedElements from './Content/NoSupportedElements';
import ProjectCancelled from './Content/ProjectCancelled';
import RemoveLanguage from './Content/RemoveLanguage';
import StepUnmodifiable from './Content/StepUnmodifiable';
import ItemNotSupported from './Content/ItemNotSupported';
import GenericError from './Content/GenericError';

interface IState extends IDialogOpenPayload {
  open: boolean;
}

type PropType = WithTranslation;

class CustomDialog extends Component<PropType, IState> {
  constructor(props: PropType) {
    super(props);
    this.state = {
      open: false,
    };
    this.unsubscribe.push(
      subscribeActionAfter(dialogOpen.type, (action) => {
        this.setState({
          open: true,
          ...(action as PayloadAction<IDialogOpenPayload>).payload,
        });
      }),
    );
    this.unsubscribe.push(
      subscribeActionAfter(dialogClose.type, () => {
        this.setState({ open: false });
      }),
    );
  }

  componentWillUnmount(): void {
    this.unsubscribe.forEach((unsub) => unsub());
  }

  private unsubscribe: Array<() => void> = [];

  private getVariantText = (): string | undefined => {
    const { variant } = this.state;

    switch (variant) {
      case DialogVariantEnum.SIMILAR_OPERATION: {
        return 'modal.confirm';
      }
      case DialogVariantEnum.IMPORT: {
        return 'modal.import';
      }
      case DialogVariantEnum.DELETE: {
        return 'modal.delete';
      }
      case DialogVariantEnum.CANCEL: {
        return 'common.cancel';
      }
      case DialogVariantEnum.SEND: {
        return 'modal.send';
      }
      case DialogVariantEnum.CHANGE_DUE_DATE_PROJECT:
      case DialogVariantEnum.CHANGE_DUE_DATE_JOB: {
        return 'modal.changeDueDate';
      }
      case DialogVariantEnum.ADD_LANGUAGES: {
        return 'modal.addLanguages';
      }
      case DialogVariantEnum.REMOVE_LANGUAGES: {
        return 'modal.removeLanguages';
      }
      case DialogVariantEnum.REMOVE_LANGUAGE: {
        return 'modal.removeLanguage';
      }
      case DialogVariantEnum.CONTENT_REMOVED:
      case DialogVariantEnum.PROJECT_CANCELLED:
      case DialogVariantEnum.IN_TRANSLATION:
      case DialogVariantEnum.NO_STATUS:
      case DialogVariantEnum.ITEM_NOT_SUPPORTED:
      case DialogVariantEnum.GENERIC_ERROR: {
        return 'modal.warning';
      }
      case DialogVariantEnum.CONTENT_CHANGED:
      case DialogVariantEnum.CONTENT_CHANGED_PROJECT: {
        return 'modal.contentChanged';
      }
      case DialogVariantEnum.NO_SUPPORTED_ELEMENT: {
        return 'modal.contentNotSent';
      }
      case DialogVariantEnum.CANCEL_UNSUCCESSFUL: {
        return 'modal.cancelUnsuccessful';
      }
      case DialogVariantEnum.VALIDATION_ERROR: {
        return 'modal.validationError';
      }
      default:
        return undefined;
    }
  };

  private getVariantComponent = (): JSX.Element | undefined => {
    const { variant, action, onClose, projectInfo, errorMessage, errorMessagesDetails } = this.state;
    const config: IJobActionProps = {
      onClose,
      jobs: projectInfo?.jobs || [],
      projectLanguages: projectInfo?.languages || [],
      projectId: projectInfo?.projectId || '',
      errorMessage: errorMessage || '',
      errorMessagesDetails: errorMessagesDetails ?? [],
    };

    switch (variant) {
      case DialogVariantEnum.IN_TRANSLATION: {
        return <InTranslation onClose={onClose} />;
      }
      case DialogVariantEnum.IMPORT:
      case DialogVariantEnum.DELETE:
      case DialogVariantEnum.CANCEL:
      case DialogVariantEnum.SEND:
      case DialogVariantEnum.SIMILAR_OPERATION: {
        return <JobAction {...config} variant={variant} action={action} />;
      }
      case DialogVariantEnum.CHANGE_DUE_DATE_PROJECT:
      case DialogVariantEnum.CHANGE_DUE_DATE_JOB: {
        return <ChangeDueDate {...config} variant={variant} />;
      }
      case DialogVariantEnum.ADD_LANGUAGES:
      case DialogVariantEnum.REMOVE_LANGUAGES: {
        return <ModifyLanguage {...config} variant={variant} />;
      }
      case DialogVariantEnum.REMOVE_LANGUAGE: {
        return <RemoveLanguage {...config} />;
      }
      case DialogVariantEnum.CONTENT_REMOVED: {
        return <ContentRemovedCms {...config} />;
      }
      case DialogVariantEnum.CONTENT_CHANGED: {
        return config.projectId ? <ContentChangedProjectCms {...config} /> : <ContentChangedCms {...config} />;
      }
      case DialogVariantEnum.NO_STATUS: {
        return <NoStatus />;
      }
      case DialogVariantEnum.ITEM_NOT_SUPPORTED: {
        return <ItemNotSupported />;
      }
      case DialogVariantEnum.PROJECT_CANCELLED: {
        return <ProjectCancelled {...config} />;
      }
      case DialogVariantEnum.WORKFLOW:
      case DialogVariantEnum.PARTIAL_WORKFLOW:
      case DialogVariantEnum.VALIDATION_ERROR:
      case DialogVariantEnum.DOWNLOAD_UNSUCCESSFUL: {
        return <StepUnmodifiable {...config} variant={variant} />;
      }
      case DialogVariantEnum.NO_SUPPORTED_ELEMENT: {
        return <NoSupportedElements {...config} />;
      }
      case DialogVariantEnum.CANCEL_UNSUCCESSFUL: {
        return <CancelUnsuccessful />;
      }
      case DialogVariantEnum.GENERIC_ERROR: {
        return <GenericError {...config} />;
      }
      default:
        return undefined;
    }
  };

  render(): JSX.Element {
    const { t } = this.props;
    const { open } = this.state;

    return (
      <Dialog open={open} BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.38)' } }}>
        <DialogTitle>
          <Typography component="span" variant="h6" color="textPrimary">
            <Box fontWeight="fontWeightBold" color="#333">
              {t(this.getVariantText() || '')}
            </Box>
          </Typography>
        </DialogTitle>
        {this.getVariantComponent()}
      </Dialog>
    );
  }
}

export default withTranslation()(CustomDialog);
